import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { v4 as randomUUID } from "uuid";
import { MusicInfo } from "../../../types/music";
import reloadPage from "../../utils/AndroidBridge";
import CustomWebSocket from "../../utils/customWebSocket";
import { isInsideDashboard } from "../../utils/isInsideDashboard";
import { useAuth } from "../useAuth";
import { useLogs } from "../useLogs";
import { toPng } from 'html-to-image';

let _socketSingleton: CustomWebSocket | null = null;
export function getSocketInstance() {
  return _socketSingleton;
}

// Reconnect backoff. Screens must never stop trying — a screen that gives up is
// a screen that stays dark until someone drives to the store — so there is no
// attempt cap, only a ceiling on the delay.
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000

function reconnectDelay(attempt: number) {
  const capped = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS)
  // Full jitter. Without it every screen in the fleet reconnects on the same
  // tick after a server restart and hammers the Durable Object in lockstep.
  return Math.random() * capped
}

export function useWebSocket(url: string, { refetchOnVisibile = true, refetchOnOnline = true } = {}) {
  const [socket, setSocket] = useState<CustomWebSocket>()

  // Refs so the timers and counters survive re-renders without re-arming.
  const attemptsRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const socketRef = useRef<CustomWebSocket>()

  const createSocket = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = undefined
    }

    const next = new CustomWebSocket(url)
    // Reset the backoff only once the connection actually establishes, not when
    // we merely attempt it.
    next.onOpenInternal = () => {
      attemptsRef.current = 0
    }
    socketRef.current = next
    setSocket(next)
  }, [url])

  // Schedules a reconnect. Safe to call from both onclose and onerror: whichever
  // fires first wins and the other is a no-op.
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return

    // A superseded socket can emit its close late, after we already replaced it.
    // Acting on that would orphan the live connection and leave two sockets
    // racing, so only reconnect when the current one is actually down.
    const current = socketRef.current
    if (current?.isOpen || current?.isConnecting) return

    const delay = reconnectDelay(attemptsRef.current)
    attemptsRef.current += 1

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = undefined
      createSocket()
    }, delay)
  }, [createSocket])

  // Reconnect immediately, bypassing backoff. For events that signal the network
  // is known-good again, where waiting out a 30s delay is pointless.
  const reconnectNow = useCallback(() => {
    const current = socketRef.current
    if (current?.isOpen || current?.isConnecting) return

    attemptsRef.current = 0
    createSocket()
  }, [createSocket])

  useEffect(() => {
    createSocket()
  }, [createSocket])

  useEffect(() => {
    return () => {
      socket?.close()
    }
  }, [socket])

  useEffect(() => {
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!refetchOnVisibile) return;

    const abortController = new AbortController()

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        reconnectNow()
      }
      // The third argument is the options bag, so the signal has to be wrapped.
      // Passing the AbortController itself meant abort() never removed the
      // listener and they stacked up across re-renders.
    }, { signal: abortController.signal })

    return () => {
      abortController.abort()
    }
  }, [refetchOnVisibile, reconnectNow])

  useEffect(() => {
    if (!refetchOnOnline) return;

    const abortController = new AbortController()

    window.addEventListener("online", () => {
      reconnectNow()
    }, { signal: abortController.signal })

    return () => {
      abortController.abort()
    }
  }, [refetchOnOnline, reconnectNow])

  return [socket, scheduleReconnect] as const
}

const screenInfoContext = createContext<ScreenSocketInfoReturnProps | null>(
  null,
);
const { Provider } = screenInfoContext;
type Props = {
  children: React.ReactNode;
  onScreenChanged: (screenInfo: any) => void;
  onLayoutChanged: (layoutInfo: any) => void;
  onPlaylistChanged: (playlistInfo: any) => void;
  onMusicChanged: (musicInfo: MusicInfo[] | undefined, action?: 'pause' | 'resume' | 'disable' | 'enable') => void;
  onMusicPaused: (musicInfo: MusicInfo[] | undefined) => void;
  onMusicResumed: (musicInfo: MusicInfo[] | undefined) => void;
  onMusicDisabled: (musicInfo: MusicInfo[] | undefined) => void;
  onMusicEnabled: (musicInfo: MusicInfo[] | undefined) => void;
  onMusicSyncEnabled: () => void;
  onMusicSyncDisabled: () => void;
};
export function ScreenSocketInfoProvider({
  children,
  onScreenChanged,
  onLayoutChanged,
  onPlaylistChanged,
  onMusicDisabled,
  onMusicEnabled,
  onMusicPaused,
  onMusicResumed,
  onMusicChanged,
  onMusicSyncEnabled,
  onMusicSyncDisabled,
}: Props) {
  const { deviceId, orgId, screenInfo: _screenInfo } = useAuth();

  const [isOnline, setIsOnline] = useState(false);

  const { log } = useLogs();

  // The org is passed so the server can route this connection to a per-org
  // Durable Object instead of the single shared one. It is ignored unless
  // SOCKET_SHARDING is enabled server-side, so sending it is always safe — which
  // is what lets the client roll out ahead of the flag being flipped.
  //
  // orgId comes out of localStorage synchronously, so a paired screen has it on
  // the first render and connects straight to the right object. An unpaired one
  // connects without it and reconnects once after pairing.
  const socketUrl =
    import.meta.env.VITE_WS_URL! +
    "/" +
    import.meta.env.VITE_WS_AUTHENTICATED_SCREEN_NAMESPACE! +
    "/public/websocket" +
    (orgId ? `?orgId=${encodeURIComponent(orgId)}` : "");

  const [socket, scheduleReconnect] = useWebSocket(
    socketUrl,
    {
      // Reconnect as soon as the OS reports the network is back, rather than
      // waiting out the backoff. On Android and Vega this is the only recovery
      // signal available; only the Tizen shell reloads on network change.
      refetchOnVisibile: false,
      refetchOnOnline: true,
    }
  )

  useEffect(() => {
    function setupSocket() {
      if (!socket) return
      setIsOnline(false);

      const _previousUUid = localStorage.getItem("previous_uuid");

      const uuid = _previousUUid ?? randomUUID();
      localStorage.setItem("uuid", uuid);

      const handleOpen = () => {
        setIsOnline(false);

        if (deviceId)
          socket.emit(
            "joinRoom",
            {
              roomId: deviceId,
              id: deviceId,
              uniqueId: uuid,
              orgId: orgId,
              isFromReload: _previousUUid ? true : false,
              from: isInsideDashboard() ? "dashboard" : "player",
            },
            () => {
              setIsOnline(true);
              localStorage.removeItem("previous_uuid");
            },
          );

        log({
          name: "SocketConnected",
          eventtype: "SocketEvent",
          payload: {
            deviceId,
          },
        });
      };

      socket.onOpen = handleOpen;

      // The socket starts connecting the moment it is constructed, but this
      // effect only runs on the next render. On slow TV hardware the handshake
      // can win that race, and the open event would fire into a null handler —
      // leaving the player connected but never joined to its room, i.e. offline
      // in the dashboard and receiving nothing.
      if (socket.isOpen) handleOpen();

      socket.onClose = () => {
        setIsOnline(false);
        log({
          name: "PairSocketDisconnected",
          eventtype: "SocketEvent",
          payload: {
            deviceId,
          },
        });
        scheduleReconnect()
      };

      socket.onError = () => {
        setIsOnline(false);
        // Most browsers fire `error` then `close`, but some TV WebViews leave an
        // errored socket half-open with no close at all. scheduleReconnect is
        // idempotent, so covering both paths is safe.
        scheduleReconnect()
      };

      // Function to handle screenshot request
      const handleScreenshotRequest = async () => {
        try {
          // Get the main content container to capture - typically the body or a main element
          const targetElement = document.getElementById('renderRoot');

          if (!targetElement) {
            log({
              name: "ScreenshotError",
              eventtype: "Error",
              payload: {
                error: "Target element not found for screenshot",
              },
            });
            return;
          }

          // Convert the element to PNG image
          const dataUrl = await toPng(targetElement as HTMLElement, {
            backgroundColor: '#ffffff',
          });

          // Extract the base64 data from the data URL
          const base64Data = dataUrl.replace(/^data:image\/png;base64,/, "");

          // Send the screenshot data to the server via HTTP request to upload to Cloudflare
          try {
            const response = await fetch(`${import.meta.env.VITE_SERVER_URL}/uploadScreenshot`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}` // Assuming auth token is stored
              },
              body: JSON.stringify({
                screenshot: base64Data,
                screen_id: deviceId,
                org_id: orgId,
                timestamp: Date.now()
              })
            });

            if (response.ok) {
              const result = await response.json();
              log({
                name: "ScreenshotUploaded",
                eventtype: "ScreenshotEvent",
                payload: {
                  deviceId: deviceId,
                  orgId: orgId,
                  assetId: result.id
                },
              });

              // Emit success event back to the main app/dashboard
              socket?.emit("broadcastMessage", {
                roomId: deviceId ?? "",
                messageContent: "screenshotUploaded",
                messageData: {
                  from: "player",
                  orgId: orgId,
                  assetId: result.id,
                  url: result.url || result.result?.variants?.[0], // Cloudflare typically returns the URL
                  timestamp: Date.now()
                },
              })
            } else {
              throw new Error(`Upload failed with status ${response.status}`);
            }
          } catch (uploadError) {
            log({
              name: "ScreenshotUploadError",
              eventtype: "Error",
              payload: {
                error: uploadError,
              },
            });
          }

          log({
            name: "ScreenshotCaptured",
            eventtype: "ScreenshotEvent",
            payload: {
              deviceId: deviceId,
              orgId: orgId,
            },
          });
        } catch (error) {
          log({
            name: "ScreenshotError",
            eventtype: "Error",
            payload: {
              error: error,
            },
          });
        }
      };

      socket.onMessage = (message) => {
        try {
          const data = JSON.parse(message.data);
          const event = data.event;
          log({
            name: "SocketEvent",
            eventtype: "SocketEvent",
            payload: {
              data: data,
            },
          });

          switch (event) {
            case "broadcastMessage": {
              const _data = data.data;
              const isFromPlayer = _data.data?.from === "player"
              const playerId = _data.from
              if (isFromPlayer && playerId !== deviceId) {
                switch (_data.content) {
                  case "groupMusicPaused":
                    onMusicPaused(_data.data)
                    break
                  case "groupMusicResumed":
                    onMusicResumed(_data.data)
                }
              } else {
                switch (_data.content) {
                  case "restart":
                    reloadPage();
                    break;
                  case "musicPauseRequested":
                  case "groupMusicPauseRequested":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicPaused(_data.data)
                    break;
                  case "musicChanged":
                  case "groupMusicChanged":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicChanged(_data.data);
                    break;
                  case "musicResumeRequested":
                  case "groupMusicResumeRequested":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicResumed(_data.data);
                    break;
                  case "musicDisabled":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicDisabled(_data.data);
                    break;
                  case "musicEnabled":
                    log({
                      name: "MusicChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    onMusicEnabled(_data.data);
                    break;
                  case "musicSyncEnabled":
                    onMusicSyncEnabled();
                    break;
                  case "musicSyncDisabled":
                    onMusicSyncDisabled()
                    break;
                  case "screenChanged":
                    log({
                      name: "ScreenChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data.data,
                      },
                    });
                    onScreenChanged(_data.data);
                    break;
                  case "layoutChanged":
                    log({
                      name: "LayoutChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: data.data,
                      },
                    });
                    onLayoutChanged(_data.data);
                    break;
                  case "playlistChanged":
                    log({
                      name: "PlaylistChanged",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: data.data,
                      },
                    });
                    onPlaylistChanged(_data.data);
                    break;
                  case "screenshot":
                    log({
                      name: "ScreenshotRequested",
                      eventtype: "SocketListenEvent",
                      payload: {
                        data: _data,
                      },
                    });
                    // Trigger screenshot capture
                    handleScreenshotRequest();
                    break;
                }
              }
              break;
            }

            case "heartbeat":
              socket.emit("heartBeatBack", {});
              break;

            default:
              break;
          }
        } catch (e) {
          log({
            name: "SocketError",
            eventtype: "Error",
            payload: {
              error: e,
            },
          });
        }
      };
    }
    setupSocket()
    _socketSingleton = socket ?? null
  }, [socket, deviceId]);

  return (
    <Provider
      value={{
        socket: socket || null,
        isOnline,
      }}
    >
      {children}
    </Provider>
  );
}

interface ScreenSocketInfoReturnProps {
  socket: CustomWebSocket | null;
  isOnline: boolean;
}

export const useScreenSocketInfo: () => ScreenSocketInfoReturnProps = () => {
  const context = useContext(screenInfoContext);
  if (!context)
    throw "useScreenSocket must be used within a ScreenSocketProvider";
  return context;
};
